import { ColumnInformationDto } from "components/annotations/interface";
import { AitRowGroupData } from "./AitRowGroupData";
import { AitHeaderGroupData } from "./interface";
import { newCell, newHeaderCell } from "./newCell";

/**
 * Insert a new column into the table data structures.
 *
 * This function updates the body rows by inserting a new blank cell at
 * `columnIndex + 1` in each row, updates header rows (honouring existing
 * colSpan behaviour) and inserts a new entry into `columnInformation` with
 * a generated `columnId`.
 *
 * @template T - The cell payload type used by the table
 * @param headerData - The existing header group data, or `false` when no header
 * @param bodyData - Array of row groups representing the table body
 * @param columnIndex - Index before which the new column will be inserted (-1 to insert at start)
 * @param defaultCellWidth - Width to use for newly created cells
 * @param blankT - Default cell payload (blank) of type T used for new cells
 * @param columnInformation - Array of ColumnInformationDto to update with the new column entry
 * @param newLength - Desired new total number of columns; if less than or equal to current length, no change is made
 * @returns An object containing the updated `headerData`, `bodyData` and `columnInformation`.
 * @throws Error when inputs are invalid (for example: empty body, out-of-range columnIndex)
 */
export const trAddColumn = <T>(
  headerData: AitHeaderGroupData<T> | false,
  bodyData: AitRowGroupData<T>[],
  columnIndex: number,
  defaultCellWidth: number,
  blankT: T,
  columnInformation: ColumnInformationDto[],
  newLength: number,
): {
  headerData: AitHeaderGroupData<T> | false;
  bodyData: AitRowGroupData<T>[];
  columnInformation: ColumnInformationDto[];
} => {
  // Check column index is acceptable
  if (columnIndex < -1) throw new Error("Column index is too low");
  if (bodyData.length === 0) throw new Error("No row groups in body data");
  if (bodyData[0].rows.length === 0) throw new Error("No rows in body data");
  if (columnIndex >= bodyData[0].rows[0].cells.length) throw new Error("Column index is too high");

  if (newLength <= bodyData[0].rows[0].cells.length)
    return {
      headerData,
      bodyData,
      columnInformation,
    };

  // Generate a new columnId for the new column
  const newColumnId = crypto.randomUUID();

  // Update columnInformation array
  let updatedColumnInformation = columnInformation.map((ci) =>
    ci.originalColumnIndex > columnIndex
      ? { ...ci, originalColumnIndex: ci.originalColumnIndex + 1 }
      : ci,
  );

  // Add new columnInformation entry for the added column (with empty annotation)
  /* istanbul ignore else */
  if (updatedColumnInformation) {
    const newColumnInfo: ColumnInformationDto = {
      columnId: newColumnId,
      originalColumnIndex: columnIndex + 1,
    };
    updatedColumnInformation = [
      ...updatedColumnInformation.slice(0, columnIndex + 1),
      newColumnInfo,
      ...updatedColumnInformation.slice(columnIndex + 1),
    ];
  }

  // Update body data
  const newBody = bodyData.map((rg) => ({
    ...rg,
    rows: rg.rows.map((r) => {
      const newCellData = newCell<T>(defaultCellWidth, blankT, newColumnId);
      return {
        ...r,
        cells: [
          ...r.cells.slice(0, columnIndex + 1),
          newCellData,
          ...r.cells.slice(columnIndex + 1),
        ],
      };
    }),
  }));
  // Update header group
  const newHeader = headerData
    ? {
        ...headerData,
        rows: headerData.rows.map((r) => {
          const cells = r.cells;
          if (columnIndex >= 0 && cells[columnIndex + 1]?.colSpan === 0) {
            let lookback = 1;
            while (lookback <= columnIndex && cells[columnIndex + 1 - lookback].colSpan === 0) {
              lookback++;
            }
            const spannerIx = columnIndex + 1 - lookback;
            // Only clone the spanner cell
            const spannerCell = {
              ...cells[spannerIx],
              colSpan: cells[spannerIx].colSpan + 1,
            };
            // Add in blank cell
            const newHeaderCellData = newHeaderCell<T>(defaultCellWidth, blankT, newColumnId);
            newHeaderCellData.colSpan = 0;
            const newCells = [
              ...cells.slice(0, columnIndex + 1),
              newHeaderCellData,
              ...cells.slice(columnIndex + 1),
            ];
            // Update the spanner cell in the new cells
            newCells[spannerIx] = spannerCell;
            return { ...r, cells: newCells };
          } else {
            // Only insert a new cell, reuse others
            const newHeaderCellData = newHeaderCell<T>(defaultCellWidth, blankT, newColumnId);
            const newCells = [
              ...cells.slice(0, columnIndex + 1),
              newHeaderCellData,
              ...cells.slice(columnIndex + 1),
            ];
            return {
              ...r,
              cells: newCells,
            };
          }
        }),
      }
    : false;
  return { headerData: newHeader, bodyData: newBody, columnInformation: updatedColumnInformation };
};
